import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Instagram, School, User2 } from "lucide-react";
import { StickerCard } from "../Sticker";
import ProfileThumbnail from "../ProfileThumbnail";
import { formatInstagramHandle, instagramHref } from "../../lib/instagram";
import LinkifiedText from "../LinkifiedText";
import PublicListingsBody from "./PublicListingUi";
import NextEventFlyer from "./NextEventFlyer";
import {
  hostEventDisplayTitle,
  splitNextEvent,
} from "../../lib/publicListingFormat";
import { listingDrawerState } from "../../lib/listingDrawer";
import HostThemedShell from "../ui/public/HostThemedShell";
import { DEFAULT_HOST_THEME, normalizeHostTheme } from "../../lib/hostThemes";

const HEADER_CONTACT_CLASS =
  "inline-flex items-center gap-1.5 min-h-[44px] font-ui font-medium text-[13px] text-live hover:text-cream transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-live/45 focus-visible:outline-offset-2 rounded-sm";

/**
 * Public host profile body. Pass `theme` to override the owner theme (preview).
 * `interactive={false}` disables links for in-drawer previews.
 */
export default function HostProfileView({
  host,
  schools = [],
  classes = [],
  events = [],
  theme,
  interactive = true,
  leading = null,
  className = "",
  testIdPrefix = "host-profile",
}) {
  const location = useLocation();
  const linkState = interactive ? listingDrawerState(location) : undefined;
  const primarySchool = schools[0] || null;
  const headerSchoolName = host?.runs_school
    ? (host?.school_name || primarySchool?.name || null)
    : null;

  const schoolPagePath = useMemo(() => {
    if (!host?.runs_school || !primarySchool?.slug) return null;
    return `/school/${primarySchool.slug}`;
  }, [host?.runs_school, primarySchool?.slug]);

  const { nextEvent, listedEvents } = useMemo(
    () => splitNextEvent(events, { preferTonight: true }),
    [events],
  );

  if (!host) return null;

  const ig = host.runs_school ? host.school_instagram : null;
  const igHref = instagramHref(ig);
  const igLabel = formatInstagramHandle(ig);
  const hasSchoolDetails = Boolean(
    host.runs_school && (host.school_name || host.school_bio),
  );
  const schoolSummaryLabel = headerSchoolName || "School details";
  const ownerTheme = normalizeHostTheme(theme ?? host.theme ?? DEFAULT_HOST_THEME);
  const previewLock = interactive
    ? ""
    : "[&_a]:pointer-events-none [&_a]:cursor-default [&_button]:pointer-events-none";

  return (
    <HostThemedShell
      theme={ownerTheme}
      syncDocument={interactive}
      className={`min-h-full bg-canvas font-body text-ink ${previewLock} ${className}`}
      data-testid={`${testIdPrefix}-themed`}
    >
      <div className="max-w-page mx-auto px-4 pt-5 pb-10 space-y-5">
        {leading}
        <StickerCard
          hero
          color="night"
          className="relative overflow-hidden p-5 sm:p-6"
          data-testid={`${testIdPrefix}-header`}
        >
          <div
            className="absolute inset-0 bg-gradient-to-br from-plum/50 via-transparent to-accent/20 pointer-events-none"
            aria-hidden
          />
          <div className="relative flex items-start gap-4">
            <ProfileThumbnail
              name={host.name}
              thumbnailUrl={host.thumbnail_url}
              size="lg"
              className="bg-pinky ring-2 ring-cream/25 host-profile-thumb-ring"
            />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1
                  className="font-display font-bold text-[32px] sm:text-[40px] leading-[1.02] text-balance text-cream"
                  data-testid={`${testIdPrefix}-name`}
                >
                  {host.name}
                </h1>
                {host.verified_host && (
                  <span
                    className="host-verified-pill inline-flex items-center rounded-pill bg-accent px-2.5 py-0.5 font-ui font-semibold text-[11px] text-night"
                    title="Trusted host on rsvp.dance"
                    aria-label="Verified host on rsvp.dance"
                    data-testid={`${testIdPrefix}-verified`}
                  >
                    Verified host
                  </span>
                )}
              </div>
              {(host.base_city || (igHref && igLabel)) && (
                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0">
                  {host.base_city && (
                    <span className="font-ui font-medium text-[14px] text-cream/75">{host.base_city}</span>
                  )}
                  {igHref && igLabel && (
                    <a
                      href={igHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Instagram: ${igLabel}`}
                      className={HEADER_CONTACT_CLASS}
                      data-testid={`${testIdPrefix}-instagram`}
                      tabIndex={interactive ? undefined : -1}
                    >
                      <Instagram className="w-3.5 h-3.5" strokeWidth={2.25} aria-hidden />
                      {igLabel}
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

          {hasSchoolDetails && (
            <details
              className="relative mt-5 pt-4 border-t border-cream/15 group"
              data-testid={`${testIdPrefix}-school-details`}
            >
              <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden flex items-center justify-between gap-2 min-h-[44px] rounded-md2 -mx-1 px-1 font-ui font-semibold text-[15px] text-cream focus-visible:outline focus-visible:outline-2 focus-visible:outline-live/45 focus-visible:outline-offset-2">
                <span className="inline-flex items-center gap-1.5 min-w-0">
                  <School className="w-4 h-4 shrink-0 text-live" strokeWidth={2.25} aria-hidden />
                  <span className="truncate">{schoolSummaryLabel}</span>
                </span>
                <ChevronDown
                  className="w-5 h-5 shrink-0 text-cream/70 transition-transform motion-reduce:transition-none group-open:rotate-180"
                  strokeWidth={2.5}
                  aria-hidden
                />
              </summary>
              <div className="pt-3 space-y-2">
                {schoolPagePath && (
                  <p className="font-ui font-semibold text-[15px]">
                    <Link
                      to={schoolPagePath}
                      className="text-live hover:text-cream transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-live/45 focus-visible:outline-offset-2 rounded-sm"
                      data-testid={`${testIdPrefix}-school-link`}
                      tabIndex={interactive ? undefined : -1}
                    >
                      View school page
                    </Link>
                  </p>
                )}
                {host.school_city && (
                  <p className="font-ui text-[13px] font-medium text-cream/70">{host.school_city}</p>
                )}
                {host.school_bio && (
                  <p className="font-ui text-[15px] font-medium text-cream/90 leading-relaxed text-pretty whitespace-pre-line">
                    <LinkifiedText>{host.school_bio}</LinkifiedText>
                  </p>
                )}
              </div>
            </details>
          )}

          {!hasSchoolDetails && !ig && (
            <p className="relative mt-5 font-ui text-[14px] font-medium text-cream/70 flex items-center gap-2">
              <User2 className="w-4 h-4" strokeWidth={2.25} aria-hidden />
              This host hasn&apos;t added school details yet.
            </p>
          )}
        </StickerCard>

        <NextEventFlyer
          event={nextEvent}
          testIdPrefix={testIdPrefix}
          titleFn={hostEventDisplayTitle}
          linkState={linkState}
        />

        <PublicListingsBody
          classes={classes}
          events={listedEvents}
          headerSchoolName={headerSchoolName}
          emptyName={host.name}
          sectionOrder="host"
          testIdPrefix={testIdPrefix}
          promoteNextEvent={false}
          hasExternalNextEvent={Boolean(nextEvent)}
          eventTitleFn={hostEventDisplayTitle}
          drawerLinks={interactive}
        />
      </div>
    </HostThemedShell>
  );
}
